import axios from "axios";
import { API_BASE } from "../config/env";

// ── Helper: POST with FormData ──────────────────────────────────────────────
const post = (endpoint, fields = {}) => {
  const fd = new FormData();
  Object.entries(fields).forEach(([k, v]) => {
    if (v !== undefined && v !== null) fd.append(k, v);
  });
  return axios
    .post(`${API_BASE}${endpoint}`, fd)
    .then((r) => r.data)
    .catch(() => null);
};

// ── Auth ────────────────────────────────────────────────────────────────────
export const apiLogin = (email, password) =>
  post("check-login", { email, password });
export const apiVerifyLoginOtp = (user_slug, otp_code) =>
  post("verify-login-otp", { user_slug, otp_code });
export const apiForgotPassword = (email) => post("forgot-password", { email });
export const apiResetPassword = (token, password) =>
  post("reset-password", { token, password });

// ── Profile / User ──────────────────────────────────────────────────────────
export const apiGetProfile = (user_slug) =>
  post("get-profile-information", { user_slug });
export const apiGetFirmDetails = (user_slug) =>
  post("get-user-firm-details", { user_slug });
export const apiGetFirmOwner = (user_slug) =>
  post("get-firm-owner-details", { user_slug });
export const apiUpdateBasicInfo = (data) =>
  post("update-profile-basic-info-v2", data);
export const apiUpdateContactInfo = (data) =>
  post("update-profile-contact-info", data);
export const apiUpdatePassword = (data) =>
  post("update-profile-password", data);
export const apiUploadAvatar = (user_slug, file) =>
  post("user-avatar-upload", { user_slug, file });
export const apiUploadFirmLogo = (user_slug, file) =>
  post("firm-logo-upload", { user_slug, file });
export const apiUpdateFirm = (data) => post("update-firm-basic-info", data);
export const apiGetUserDetails = (user_slug) =>
  post("get-user-details", { user_slug });
export const apiUpdateSummary = (data) => post("update-summary-details", data);

// ── Dashboard ───────────────────────────────────────────────────────────────
export const apiGetDashboardCounts = (user_slug) =>
  post("get-dashboard-counts", { user_slug });
export const apiGetRecentSearches = (user_slug) =>
  post("get-recent-searches", { user_slug });
export const apiFavoriteProfiles = (user_slug) =>
  post("favorite-profiles", { user_slug });
export const apiGetFirmsInNetwork = (user_slug) =>
  post("get-firms-in-network", { user_slug });

// ── Resources ───────────────────────────────────────────────────────────────
export const apiGetMyResources = (user_slug, filter_text = "") =>
  post("get-my-resource-listing", { user_slug, filter_text });
export const apiGetResourceDetails = (user_slug, resource_slug) =>
  post("get-resource-details", { user_slug, resource_slug });
export const apiCreateResource = (data) => post("create-resource", data);
export const apiCreateResourceFromResume = (user_slug, file) =>
  post("create-resource-from-resume", { user_slug, file, resume_upload: true });
export const apiCreateResourceFromCSV = (user_slug, file) =>
  post("upload-resource-csv", { user_slug, file });
export const apiChangeFavorite = (user_id, resource_id) =>
  post("change-favorite", { user_id, resource_id });
export const apiGetResourceBasicDetails = (user_slug, resource_slug) =>
  post("get-resource-basic-details", { user_slug, resource_slug });
export const apiCheckEditAccess = (user_slug, resource_slug) =>
  post("get-resource-edit-access", { user_slug, resource_slug });

// ── Education ───────────────────────────────────────────────────────────────
export const apiGetEducation = (user_slug) =>
  post("get-education-details", { user_slug });
export const apiSaveEducation = (data) => post("save-education-details", data);
export const apiUpdateEducation = (data) =>
  post("update-education-details", data);
export const apiRemoveEducation = (data) =>
  post("remove-education-details", data);

// ── Employment ──────────────────────────────────────────────────────────────
export const apiGetEmployment = (user_slug) =>
  post("get-employment-details", { user_slug });
export const apiSaveEmployment = (data) =>
  post("save-employment-details", data);
export const apiUpdateEmployment = (data) =>
  post("update-employment-details", data);
export const apiRemoveEmployment = (data) =>
  post("remove-employment-details", data);

// ── Skills ──────────────────────────────────────────────────────────────────
export const apiGetSkillsBySearch = (search_text) =>
  post("get-skills-by-search-text", { search_text });
export const apiAddSkill = (data) => post("add-new-skill", data);
export const apiRemoveSkill = (data) => post("remove-skill", data);
export const apiGetAllRawSkills = (user_slug) =>
  post("get-all-raw-skills", { user_slug });

// ── Certifications ──────────────────────────────────────────────────────────
export const apiAddCertification = (data) => post("add-certification", data);
export const apiRemoveCertification = (data) =>
  post("remove-certification", data);

// ── Languages ───────────────────────────────────────────────────────────────
export const apiGetLanguagesBySearch = (search_text) =>
  post("get-languages-by-search-text", { search_text });
export const apiAddLanguage = (data) => post("add-new-language", data);
export const apiRemoveLanguage = (data) => post("remove-language", data);

// ── Documents ───────────────────────────────────────────────────────────────
export const apiSaveDocument = (data) => post("save-uploaded-document", data);
export const apiUpdateDocument = (data) => post("update-document", data);
export const apiRemoveDocument = (data) => post("remove-document", data);

// ── Availability ────────────────────────────────────────────────────────────
export const apiSaveAvailability = (data) =>
  post("save-resource-availability", data);
export const apiUpdateIsAdvertised = (data) =>
  post("update-is-advertised", data);
export const apiUpdateSelfService = (data) => post("update-self-service", data);

// ── Jobs ─────────────────────────────────────────────────────────────────────
export const apiGetLatestJobs = (user_slug, search_text = "") =>
  post("get-latest-job-postings", { user_slug, search_text });
export const apiGetMyJobs = (user_slug, search_text = "") =>
  post("get-my-job-postings", { user_slug, search_text });
export const apiGetJobDetails = (job_slug, user_slug) =>
  post("get-job-details", { job_slug, user_slug });
export const apiGetJobEditDetails = (job_slug, user_slug) =>
  post("get-job-edit-details", { job_slug, user_slug });
export const apiCreateJob = (data) => post("create-job", data);
export const apiCreateJobViaParsing = (user_slug, file) =>
  post("create-job-via-parsing", { user_slug, file });
export const apiCreateJobViaContent = (user_slug, content) =>
  post("create-job-via-content", { user_slug, content });
export const apiUpdateJob = (data) => post("update-job", data);
export const apiRemoveJob = (data) => post("remove-job", data);

// ── Job Matching ─────────────────────────────────────────────────────────────
export const apiGetMatchingCandidates = (job_slug, user_slug) =>
  post("ai-matching-v2", { job_slug, user_slug });
export const apiGetJobRequirement = (job_slug, user_slug) =>
  post("get-job-requirement", { job_slug, user_slug });
export const apiSaveJobRequirement = (data) =>
  post("save-job-requirement", data);
export const apiGetAvailableProposeJob = (data) =>
  post("get-available-propose-job", data);
export const apiAddProposeJob = (data) => post("add-propose-job", data);

// ── Access Control / Clients ─────────────────────────────────────────────────
export const apiGetMyClients = (user_slug) =>
  post("get-my-clients", { user_slug });
export const apiGetClientsByFirm = (user_slug, firm_id) =>
  post("get-clients-by-firm", { user_slug, firm_id });
export const apiCreateClient = (data) => post("create-client", data);
export const apiRemoveClientAccess = (data) =>
  post("remove-client-access", data);
export const apiGetFirmAccessList = (user_slug) =>
  post("get-all-firm-access-list", { user_slug });

// ── Search ────────────────────────────────────────────────────────────────────
export const apiSearchV2 = (params) => {
  const fd = new FormData();
  fd.append("params", JSON.stringify(params));
  const axios = require("axios");
  return axios.default
    .post(`${API_BASE}get-search-result-v2`, fd)
    .then((r) => r.data)
    .catch(() => null);
};
export const apiSearch = (keyword, location, availability, user_slug) =>
  post("get-search-result-v2", { keyword, location, availability, user_slug });
export const apiSearchAutoComplete = (search_text, user_slug) =>
  post("get-autocomplete-main-result", { keyword: search_text, user_slug });
export const apiFieldAutoComplete = (type, keyword) =>
  post("get-autocomplete-result", { type, keyword });
export const apiLocationAutoComplete = (search_text) =>
  post("get-location-search-auto-complete", { search_text });

// ── Notes ─────────────────────────────────────────────────────────────────────
export const apiCreateNote = (data) => post("create-note", data);
export const apiUpdateNote = (data) => post("update-note", data);
export const apiRemoveNote = (data) => post("remove-note", data);
export const apiGetNote = (data) => post("get-note", data);

// ── Recommendations ───────────────────────────────────────────────────────────
export const apiSendRecommendation = (data) =>
  post("send-recommendation", data);
export const apiGetMyRecommendations = (user_slug) =>
  post("get-my-recommendation", { user_slug });
export const apiGetRecommendationHistory = (user_slug) =>
  post("get-my-recommendation-history", { user_slug });
export const apiRemoveRecommendation = (data) =>
  post("remove-recommendation-history", data);

// ── Contact ───────────────────────────────────────────────────────────────────
export const apiSendQuery = (data) => post("send-query", data);
export const apiSendContactMessage = (data) =>
  post("send-contact-message", data);

// ── ConnectIQ AI ─────────────────────────────────────────────────────────────
export const apiConnectIQChat = (data) => post("connectiq/chat", data);
export const apiGetConnectIQSessions = (user_slug) =>
  post("connectiq/get_sessions", { user_slug });
export const apiGetConnectIQMessages = (data) =>
  post("connectiq/get_messages", data);
export const apiDeleteConnectIQSession = (data) =>
  post("connectiq/delete_session", data);
export const apiGetFirmUsage = (user_slug) =>
  post("connectiq/get_firm_usage", { user_slug });
